/**
 * Zahnheld account entry.
 *
 * <shopify-account> is a Shopify-controlled element: it renders its own avatar, that avatar is the
 * only thing you can click, and when signed in the avatar is a bare initial on a disc. The drawer
 * footer therefore pairs it with a label of our own, and this element forwards a click on that
 * label into the component.
 *
 * showModal() and close() are public methods on the element but are not part of the documented
 * API, so Shopify can drop them without warning. Every step is guarded: showModal() first, then a
 * click on the avatar button inside the open shadow root, then the account page as a last resort.
 */
(() => {
  const ACCOUNT_TAG = 'shopify-account';

  /** Opens the account sheet on a specific <shopify-account>. Returns false if it could not. */
  function openSheet(account) {
    if (!account) return false;

    if (typeof account.showModal === 'function') {
      try {
        account.showModal();
        return true;
      } catch (error) {
        console.warn('[zh-account] showModal failed:', error);
      }
    }

    const button = account.shadowRoot && account.shadowRoot.querySelector('button');
    if (button) {
      button.click();
      return true;
    }

    return false;
  }

  /**
   * A sheet only renders where the component is part of the box tree. The header instance is
   * display:none below the sm breakpoint, and the drawer instance is inside a closed drawer, so an
   * instance can exist and still be unable to show anything.
   */
  function isRenderable(account) {
    return Boolean(account && account.getClientRects().length > 0);
  }

  function accountRoute() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return `${root.replace(/\/$/, '')}/account`;
  }

  window.ZhAccount = {
    /**
     * Opens the account sheet. Pass an instance to target it, otherwise the first one that can
     * actually paint wins. Navigates to the account page when no instance qualifies.
     */
    open(account) {
      const target = account || Array.from(document.querySelectorAll(ACCOUNT_TAG)).find(isRenderable);

      if (openSheet(target)) return true;

      window.location.href = accountRoute();
      return false;
    },
  };

  class ZhAccountEntry extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.addEventListener('click', (event) => this.onClick(event));
    }

    /**
     * Clicks inside the component (including inside its shadow root, which retargets to the host)
     * are already handled by Shopify. Only the surrounding label needs forwarding, otherwise the
     * sheet would open and close again on the same tap.
     */
    onClick(event) {
      const account = this.querySelector(ACCOUNT_TAG);
      if (!account || account === event.target || account.contains(event.target)) return;

      event.preventDefault();
      window.ZhAccount.open(account);
    }
  }

  if (!customElements.get('zh-account-entry')) {
    customElements.define('zh-account-entry', ZhAccountEntry);
  }
})();
